import { createAdProvider, TestAdProvider, type AdOutcome } from './ads';
import { createPurchaseProvider, TestPurchaseProvider, type StoreProduct } from './store';
import { PRODUCTS, bestValue, grantPurchase, restorePurchases, type ProductDef, type ProductId } from './products';
import type { SaveData } from '../meta/progress';

export type BuyOutcome = 'ok' | 'cancelled' | 'failed' | 'busy';

/**
 * Single entry point for purchases and rewarded videos. Guards against
 * double taps (one transaction at a time, §14) and credits purchases to
 * the save game exactly once per store transaction.
 */
export class Commerce {
  readonly purchases = createPurchaseProvider();
  readonly ads = createAdProvider();
  private prices = new Map<ProductId, StoreProduct>();
  private busy = false;
  private adsStarted?: Promise<void>;

  constructor(
    private readonly getSave: () => SaveData,
    private readonly setSave: (s: SaveData) => void,
  ) {}

  get testMode(): boolean {
    return this.purchases.mode === 'test';
  }

  get isBusy(): boolean {
    return this.busy;
  }

  /** UI hooks for TEST MODE (fake purchase sheet / fake video). */
  setTestUi(confirm: (p: ProductDef, price: string) => Promise<boolean>, playVideo: () => Promise<boolean>): void {
    if (this.purchases instanceof TestPurchaseProvider) this.purchases.confirm = confirm;
    if (this.ads instanceof TestAdProvider) this.ads.play = playVideo;
  }

  async init(): Promise<void> {
    try {
      await this.purchases.init();
      for (const p of await this.purchases.products()) this.prices.set(p.id, p);
    } catch (e) {
      console.warn('store not available', e);
    }
  }

  /** Ads start later (consent + tracking prompt), not on the very first launch. */
  startAds(): Promise<void> {
    this.adsStarted ??= this.ads.init().catch((e) => console.warn('ads not available', e));
    return this.adsStarted;
  }

  price(id: ProductId): StoreProduct | undefined {
    return this.prices.get(id);
  }

  bestValue(): ProductId | null {
    const prices: Partial<Record<ProductId, number>> = {};
    for (const p of PRODUCTS) {
      const sp = this.prices.get(p.id);
      if (sp) prices[p.id] = sp.price;
    }
    return bestValue(prices);
  }

  async buy(id: ProductId): Promise<BuyOutcome> {
    if (this.busy) return 'busy';
    this.busy = true;
    try {
      const r = await this.purchases.purchase(id);
      if (!r.ok) return r.cancelled ? 'cancelled' : 'failed';
      const g = grantPurchase(this.getSave(), id, r.transactionId);
      this.setSave(g.save);
      return 'ok';
    } catch (e) {
      console.warn('purchase failed', e);
      return 'failed';
    } finally {
      this.busy = false;
    }
  }

  async restore(): Promise<ProductId[] | null> {
    if (this.busy) return null;
    this.busy = true;
    try {
      const owned = await this.purchases.restore();
      const r = restorePurchases(this.getSave(), owned);
      this.setSave(r.save);
      return r.restored;
    } catch (e) {
      console.warn('restore failed', e);
      return null;
    } finally {
      this.busy = false;
    }
  }

  async watchAd(): Promise<AdOutcome> {
    if (this.busy) return 'unavailable';
    this.busy = true;
    try {
      await this.startAds();
      return await this.ads.showRewarded();
    } finally {
      this.busy = false;
    }
  }
}
